import React from "react";
import type { ApolloClient } from "@apollo/client";
import IconSpinner from "components/IconSpinner";
import IconMessageCard from "components/IconMessageCard";
import AddEmployeeUploadFormToggle from "pages/dashboards/employerDashboard/userManagement/AddEmployeeUploadFormToggle";

import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { get, isEmpty } from "lodash";
import { FiFileText } from "react-icons/fi";
import { push } from "store/actions/router";
import { EmployeeUploadJobTable as EmployeeUploadJobTableRaw } from "pages/dashboards/employerDashboard/userManagement/EmployeeUploadJobTable";
import { getEmployeeDocumentUploadJobs } from "actions/employerActions";
import { employerConstants } from "actions/types";
import { createErrorSelector } from "store/selectors";
import type { RootState } from "types/state";

// EmployeeUploadJobTable is the raw (unconnected) class, so its props include
// the redux-injected employeeUploadJobs typed as Array<Record<string, unknown>>.
// This page renders it directly and passes this.props.employeeUploadJobs (typed
// unknown[]); cast to a loose own-prop shape so that value passes through as the
// original JS did.
const EmployeeUploadJobTable =
  EmployeeUploadJobTableRaw as unknown as React.ComponentType<{
    isIconAdmin?: boolean;
    employeeUploadJobs?: unknown[];
    companyId?: string;
  }>;

const actions = [employerConstants.GET_EMPLOYEE_DOCUMENT_UPLOAD_JOBS];

const errorSelector = createErrorSelector(actions);

const mapStateToProps = (state: RootState) => {
  return {
    error: errorSelector(state),
    employeeUploadJobs: get(state, "employer.employeeUploadJobs", []),
  };
};

const mapDispatchToProps = {
  push,
  getEmployeeDocumentUploadJobs,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  companyId?: string;
  isIconAdmin?: boolean;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  jobsForEmployer: object;
  initialFetching: boolean;
  showForm: boolean;
  // read in render (this.state.employeeUploadJobs) but never written via setState
  // found-not-fixed: render reads this.state.employeeUploadJobs (always undefined);
  // the data actually lives on this.props.employeeUploadJobs
  employeeUploadJobs?: unknown[];
}

class RosterUploads extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      jobsForEmployer: {},
      initialFetching: true,
      showForm: false,
    };
  }

  async componentDidMount() {
    await this.props.getEmployeeDocumentUploadJobs(
      this.props.client as ApolloClient<object>,
      this.props.companyId as string
    );

    this.setState({ initialFetching: false });
  }

  _showForm = () => {
    this.setState({ showForm: true });
  };

  render() {
    let content;

    if (this.state.initialFetching) {
      content = <IconSpinner centered />;
    } else if (!this.state.showForm && isEmpty(this.state.employeeUploadJobs)) {
      content = (
        <IconMessageCard
          header={"Empty Roster"}
          subheader="Employer has not uploaded a roster yet."
          icon={<FiFileText color="white" stroke="#60A4BF" size={16} />}
          actionText={"Upload File"}
          onClick={this._showForm}
        />
      );
    } else if (this.state.showForm) {
      content = (
        <AddEmployeeUploadFormToggle
          onClose={() => this.setState({ showForm: false })}
          isIconAdmin={this.props.isIconAdmin}
          companyId={this.props.companyId}
          onUploadSuccess={() => {
            this.props.push("/dashboard/users/employees");
          }}
        />
      );
    } else {
      // found-not-fixed: this JSX expression statement is never assigned to
      // `content`, so the upload-job table never renders in this branch
      <EmployeeUploadJobTable
        isIconAdmin={true}
        employeeUploadJobs={this.props.employeeUploadJobs}
        companyId={this.props.companyId}
      />;
    }

    return (
      <div id="admin-roster-page" className="roster-uploads">
        {content}
      </div>
    );
  }
}

export default connector(withApollo<Props>(RosterUploads));
